import { isToday, isPast, parseISO, endOfWeek, addWeeks } from 'date-fns'
import type { Task, TaskPriority, TaskStatus, TaskEntityType } from '@/types/api'

export const PRIORITY_BADGE: Record<TaskPriority, string> = {
  low: 'bg-muted/50 text-muted-foreground border-border',
  medium: 'bg-blue-50 text-blue-700 border-blue-200',
  high: 'bg-amber-50 text-amber-700 border-amber-200',
  urgent: 'bg-red-50 text-red-700 border-red-200',
}

export const PRIORITY_ORDER: Record<TaskPriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
}

export const PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent']

export const STATUS_LABEL: Record<TaskStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  done: 'Done',
}

export const ENTITY_TYPE_LABEL: Record<TaskEntityType, string> = {
  contact: 'Contact',
  opportunity: 'Opportunity',
  general: 'General',
  mantastic: 'Mantastic',
  delivery_project: 'Project',
}

export type TaskGroup = 'overdue' | 'today' | 'upcoming' | 'done'

export const TASK_GROUP_ORDER: TaskGroup[] = ['overdue', 'today', 'upcoming', 'done']

export const TASK_GROUP_LABEL: Record<TaskGroup, string> = {
  overdue: 'Overdue',
  today: 'Today',
  upcoming: 'Upcoming',
  done: 'Done',
}

/** Which dashboard group a task belongs to, relative to `now`. */
export function taskGroup(task: Task, now: Date = new Date()): TaskGroup {
  if (task.status === 'done') return 'done'
  const due = parseISO(task.due_date)
  if (isToday(due)) return 'today'
  if (isPast(due) && due < now) return 'overdue'
  return 'upcoming'
}

export function isOverdue(task: Task, now: Date = new Date()): boolean {
  return task.status !== 'done' && taskGroup(task, now) === 'overdue'
}

/** Sort by priority (urgent first), then by soonest due date. */
export function byPriorityThenDue(a: Task, b: Task): number {
  const p = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
  if (p !== 0) return p
  return a.due_date < b.due_date ? -1 : a.due_date > b.due_date ? 1 : 0
}

/** Sort by soonest due date, then priority (urgent first) as a tiebreak. */
export function byDueThenPriority(a: Task, b: Task): number {
  if (a.due_date !== b.due_date) return a.due_date < b.due_date ? -1 : 1
  return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
}

// — Upcoming time-window filter (used on the Tasks page to narrow the upcoming bucket) —

export type UpcomingFilter = 'all' | 'this-week' | 'next-week' | 'later'

export const UPCOMING_FILTERS: UpcomingFilter[] = ['all', 'this-week', 'next-week', 'later']

export const UPCOMING_FILTER_LABEL: Record<UpcomingFilter, string> = {
  all: 'All',
  'this-week': 'This week',
  'next-week': 'Next week',
  later: 'Later',
}

// Weeks start Monday (AEST business convention).
const WEEK_OPTS = { weekStartsOn: 1 as const }

/** Which upcoming window a future-dated task falls into, relative to `now`. */
export function upcomingWindow(task: Task, now: Date = new Date()): Exclude<UpcomingFilter, 'all'> {
  const due = parseISO(task.due_date)
  if (due <= endOfWeek(now, WEEK_OPTS)) return 'this-week'
  if (due <= endOfWeek(addWeeks(now, 1), WEEK_OPTS)) return 'next-week'
  return 'later'
}

export function matchesUpcomingFilter(task: Task, filter: UpcomingFilter, now: Date = new Date()): boolean {
  return filter === 'all' || upcomingWindow(task, now) === filter
}

/** Group tasks into the four dashboard buckets, each sorted for display. */
export function groupTasks(tasks: Task[], now: Date = new Date()): Record<TaskGroup, Task[]> {
  const groups: Record<TaskGroup, Task[]> = { overdue: [], today: [], upcoming: [], done: [] }
  for (const task of tasks) groups[taskGroup(task, now)].push(task)
  for (const key of TASK_GROUP_ORDER) groups[key].sort(byPriorityThenDue)
  return groups
}
